import { MembershipLifecycleActionForm } from '@b2b/types/membership';

/**
 * Interface for cart membership items with membershipInfo
 */
interface CartMembershipItem {
  membershipInfo?: {
    membershipCategoryId?: string;
    membershipCategoryName?: string;
    membershipOfferingName?: string;
  };
}

/**
 * Resolves the membership name for a lifecycle form by looking it up in the cart memberships.
 * Compares all membership levels in the form with all memberships in the cart to find a match.
 * Falls back to the form's membershipLevelsInfo if not found in the cart.
 * 
 * @param lifecycleForm - The lifecycle form containing membership level info
 * @param cartMemberships - Array of membership items from the cart
 * @returns The resolved membership name or empty string
 */
export function resolveMembershipNameForForm(
  lifecycleForm: MembershipLifecycleActionForm,
  cartMemberships: CartMembershipItem[]
): string {
  const membershipLevelsInfo = lifecycleForm.membershipLevelsInfo ?? [];
  
  // Try to find a matching membership in the cart
  for (const levelInfo of membershipLevelsInfo) {
    const formMembershipLevelId = levelInfo?.membershipLevel?.id;
    
    if (!formMembershipLevelId) {
      continue;
    }
    
    // Find the matching membership in the cart by comparing membership category IDs
    const matchingMembership = cartMemberships.find(
      (item) => item.membershipInfo?.membershipCategoryId === formMembershipLevelId
    );
    
    // If found in cart, use the name from the cart
    if (matchingMembership?.membershipInfo?.membershipCategoryName) {
      const { membershipCategoryName, membershipOfferingName } = matchingMembership.membershipInfo;
      return membershipOfferingName
        ? `${membershipCategoryName} - ${membershipOfferingName}`
        : membershipCategoryName;
    }
  }
  
  // Fallback: use the first membership level name from the form's data
  return membershipLevelsInfo[0]?.membershipLevel?.name ?? '';
}

